package fr.frux.core.maps

import kotlin.math.atan2
import kotlin.math.cos
import kotlin.math.sin
import kotlin.math.sqrt

/** Rayon moyen de la Terre, en metres. */
private const val EARTH_RADIUS_METERS = 6_371_008.8

/**
 * Represents a geographic location with latitude and longitude.
 */
data class Location(
    val latitude: Double,
    val longitude: Double
) {
    init {
        require(latitude in -90.0..90.0) { "Latitude must be between -90 and 90" }
        require(longitude in -180.0..180.0) { "Longitude must be between -180 and 180" }
    }

    /**
     * Distance orthodromique (haversine) vers [other], en metres.
     *
     * Sphere et non ellipsoide : l'erreur (~0,5 %) est sans consequence pour les usages du client
     * — comparer un deplacement de camera a un seuil, ordonner des arbres proches. Le calcul de
     * reference reste celui de PostGIS cote backend.
     */
    fun distanceMetersTo(other: Location): Double {
        val phi1 = latitude.toRadians()
        val phi2 = other.latitude.toRadians()
        val deltaPhi = (other.latitude - latitude).toRadians()
        val deltaLambda = (other.longitude - longitude).toRadians()

        val a = sin(deltaPhi / 2) * sin(deltaPhi / 2) +
            cos(phi1) * cos(phi2) * sin(deltaLambda / 2) * sin(deltaLambda / 2)

        return 2 * EARTH_RADIUS_METERS * atan2(sqrt(a), sqrt(1 - a))
    }
}

private fun Double.toRadians(): Double = this * kotlin.math.PI / 180
